import { BaseService } from "./base";
import { AdminUser } from "../models/adminUser";
import { Role } from "../models/role";

import { ApiRequest } from "../lib/request";
import { ApiResponse } from "../lib/response";

export class AdminUserService extends BaseService<AdminUser> {
  override model = AdminUser;
  override modelName = 'adminUser';
  override modelLabel = 'Admin User';

  async addPhoto(
    adminUser: AdminUser,
    image: File,
    onUploadProgress?: (progressEvent: import('axios').AxiosProgressEvent) => void,
  ): Promise<ApiResponse<AdminUser>> {
    const formData = new FormData();
    formData.append('image', image);

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${adminUser.objectID()}/addPhoto`,
      data: formData,
      onUploadProgress,
    });

    return await this.requestItem<AdminUser>(request, this.model);
  }

  async deletePhoto(
    adminUser: AdminUser
  ): Promise<ApiResponse<AdminUser>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${adminUser.objectID()}/deletePhoto`,
    });

    return await this.requestItem<AdminUser>(request, this.model);
  }

  override async delete(
    adminUser: AdminUser,
    retainOldAppointments: boolean = true
  ): Promise<ApiResponse<AdminUser>> {
    const request = new ApiRequest({
      method: 'DELETE',
      url: `${this.modelBaseUrl}/${adminUser.objectID()}`,
      query: { retain: retainOldAppointments ? '1' : '0' },
    });

    return await this.requestItem<AdminUser>(request, this.model);
  }

  async resetPassword({
    adminUser,
    password,
    confirm,
    sendEmail,
    revokeSessions,
  }: {
    adminUser: AdminUser;
    password: string;
    confirm: string;
    sendEmail: boolean;
    revokeSessions: boolean;
  }): Promise<ApiResponse<AdminUser>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${adminUser.objectID()}/resetPassword`,
      query: {
        password,
        confirm,
        sendEmail: sendEmail ? '1' : '0',
        revokeSessions: revokeSessions ? '1' : '0',
      },
    });

    return await this.requestItem<AdminUser>(request, this.model);
  }

  async resetPin(
    adminUser: AdminUser,
    pin: string,
    confirm: string
  ): Promise<ApiResponse<AdminUser>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${adminUser.objectID()}/resetPin`,
      data: { pin, confirm },
    });

    return await this.requestItem<AdminUser>(request, this.model);
  }

  async assignRole(
    adminUser: AdminUser,
    role: Role
  ): Promise<ApiResponse<AdminUser>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${adminUser.objectID()}/assignRole`,
      data: {
        roleType: role?.type || 'custom',
        idCustomRole: role?.idCustomRole,
      },
    });

    return await this.requestItem<AdminUser>(request, this.model);
  }

  async changeEmail({
    adminUser,
    token,
    code,
    email,
    firstName,
    lastName,
  }: {
    adminUser: AdminUser;
    token?: string;
    code?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  }): Promise<ApiResponse<AdminUser>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${adminUser.objectID()}/changeEmail`,
      data: { token, code, email, firstName, lastName },
    });

    return await this.requestItem<AdminUser>(request, this.model);
  }
} 